import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import apiClient, { getApiBaseUrl } from '../../services/api';
import ServicePageBodyEditor from './ServicePageBodyEditor';
import ServicePageBoxesEditor from './ServicePageBoxesEditor';
import {
  buildFullContentPayload,
  getAdditionalSectionsFromContent,
  getBoxesFromContent,
  getBoxesSectionTitle,
} from '../../utils/servicePageBody';

const SERVICE_PAGE_SLUGS = [
  'services',
  'medical-clinics',
  'surgical-clinics',
  'diagnostic-services',
  'emergency-services',
  'radiology',
  'interventional-radiology',
  'pathology-services',
  'admission',
  'rooms-wards',
  'medical-icu',
  'surgical-icu',
  'neonatal-icu',
  'coronary-care-unit',
  'high-dependency-unit',
  'operation-theater',
  'interventional-cardiology',
  'endoscopy-ercp',
  'echo-ett',
  'dialysis',
  'daycare',
];

const heroImageUrlFromId = (imageId) => {
  if (!imageId) return '';
  return `${getApiBaseUrl()}/images/${imageId}`;
};

const parseContentField = (content) => {
  if (!content) return null;
  if (typeof content === 'object') return content;
  if (typeof content === 'string') {
    const raw = content.trim();
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : { bodyHtml: raw };
    } catch (_e) {
      return { bodyHtml: raw };
    }
  }
  return null;
};

const normalizeServicePage = (raw) => {
  if (!raw?.slug) return null;
  const content = parseContentField(raw.content ?? raw.content_json ?? raw.contentJson);
  return {
    slug: raw.slug,
    title: raw.title || '',
    subtitle: raw.subtitle || '',
    heroImageId: raw.heroImageId || raw.hero_image_id || '',
    heroImageUrl: raw.heroImageUrl || raw.hero_image_url || '',
    introTitle: raw.introTitle || raw.intro_title || '',
    introText: raw.introText || raw.intro_text || '',
    content,
    menuGroup: raw.menuGroup || raw.menu_group || '',
    parentSlug: raw.parentSlug || raw.parent_slug || '',
    isActive: raw.isActive !== false && raw.is_active !== 0 && raw.is_active !== false,
    sortOrder: Number(raw.sortOrder ?? raw.sort_order ?? 0),
  };
};

const extractServicePages = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp.map(normalizeServicePage).filter(Boolean);
  if (Array.isArray(resp.data)) return resp.data.map(normalizeServicePage).filter(Boolean);
  return [];
};

const pageToDraft = (page, slug) => {
  const heroImageId = page.heroImageId || '';
  const heroPreview = heroImageId
    ? heroImageUrlFromId(heroImageId)
    : (page.heroImageUrl || '');

  return {
    slug: page.slug || slug,
    title: page.title || '',
    subtitle: page.subtitle || '',
    heroImageId,
    heroImageFile: null,
    heroImagePreview: heroPreview,
    introTitle: page.introTitle || '',
    introText: page.introText || '',
    contentBodyHtml: page.content?.bodyHtml || '',
    boxesSectionTitle: getBoxesSectionTitle(page.content),
    contentBoxes: getBoxesFromContent(page.content),
    contentSections: getAdditionalSectionsFromContent(page.content),
    menuGroup: page.menuGroup || '',
    parentSlug: page.parentSlug || '',
    isActive: page.isActive !== false,
    sortOrder: page.sortOrder || 0,
  };
};

const createDefaultDraft = (slug) => ({
  slug,
  title: '',
  subtitle: '',
  heroImageId: '',
  heroImageFile: null,
  heroImagePreview: '',
  introTitle: '',
  introText: '',
  contentBodyHtml: '',
  boxesSectionTitle: '',
  contentBoxes: [],
  contentSections: [],
  menuGroup: '',
  parentSlug: '',
  isActive: true,
  sortOrder: 0,
});

const ManageServicePages = () => {
  const [pages, setPages] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('services');
  const [draft, setDraft] = useState(createDefaultDraft('services'));
  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await apiClient.getServicePages();
        if (!resp?.success) {
          throw new Error(resp?.error || 'Failed to load service pages');
        }
        const items = extractServicePages(resp);
        if (mounted) {
          setPages(items);
        }
      } catch (e) {
        if (mounted) setError(e.message || 'Failed to load service pages');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const pagesBySlug = useMemo(() => {
    const map = new Map();
    pages.forEach((p) => map.set(p.slug, p));
    return map;
  }, [pages]);

  useEffect(() => {
    let mounted = true;

    const applyPage = (page) => {
      if (!mounted || !page) return;
      setDraft(pageToDraft(page, selectedSlug));
    };

    (async () => {
      setLoadingPage(true);
      setSaved('');

      const fromList = pagesBySlug.get(selectedSlug);
      if (fromList) {
        applyPage(fromList);
        setLoadingPage(false);
        return;
      }

      setDraft(createDefaultDraft(selectedSlug));

      try {
        const resp = await apiClient.getServicePage(selectedSlug);
        if (!mounted) return;
        if (resp?.success && resp.data) {
          const page = normalizeServicePage(resp.data);
          if (page) {
            applyPage(page);
            setPages((prev) => {
              const idx = prev.findIndex((p) => p.slug === selectedSlug);
              if (idx === -1) return [...prev, page];
              const clone = [...prev];
              clone[idx] = page;
              return clone;
            });
          }
        }
      } catch (e) {
        const message = e.message || '';
        const isMissing = /not found|404/i.test(message);
        if (mounted && !isMissing) {
          setError(message || 'Failed to load service page');
        }
      } finally {
        if (mounted) setLoadingPage(false);
      }
    })();

    return () => { mounted = false; };
  }, [selectedSlug, pagesBySlug]);

  const handleHeroImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError('');
    setDraft((d) => ({
      ...d,
      heroImageFile: file,
      heroImagePreview: URL.createObjectURL(file),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved('');
    try {
      const content = buildFullContentPayload({
        boxesSectionTitle: draft.boxesSectionTitle,
        boxes: draft.contentBoxes,
        sections: draft.contentSections,
      });

      const pageData = {
        title: draft.title,
        subtitle: draft.subtitle,
        introTitle: draft.introTitle,
        introText: draft.introText,
        menuGroup: draft.menuGroup,
        parentSlug: draft.parentSlug || null,
        isActive: draft.isActive,
        sortOrder: Number(draft.sortOrder) || 0,
        content,
        existingHeroImageId: draft.heroImageId || null,
      };

      const resp = draft.heroImageFile
        ? await apiClient.saveServicePageWithImage(selectedSlug, pageData, draft.heroImageFile)
        : await apiClient.saveServicePage(selectedSlug, {
          ...pageData,
          heroImageId: draft.heroImageId || null,
        });

      if (!resp?.success) throw new Error(resp?.error || 'Failed to save');

      const newPage = normalizeServicePage(resp.data) || {
        slug: selectedSlug,
        ...pageData,
        content,
      };
      setPages((prev) => {
        const idx = prev.findIndex((p) => p.slug === selectedSlug);
        if (idx === -1) return [...prev, newPage];
        const clone = [...prev];
        clone[idx] = newPage;
        return clone;
      });
      setDraft(pageToDraft(newPage, selectedSlug));
      setSaved('Saved successfully.');
    } catch (e) {
      setError(e.message || 'Failed to save service page');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Manage Service Pages
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage hero images, titles, and page content for all service pages.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, maxHeight: 640, overflow: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Service Pages</Typography>
            <List dense>
              {SERVICE_PAGE_SLUGS.map((slug) => {
                const title = pagesBySlug.get(slug)?.title;
                return (
                  <ListItemButton
                    key={slug}
                    selected={selectedSlug === slug}
                    onClick={() => setSelectedSlug(slug)}
                  >
                    <ListItemText primary={title || slug} secondary={title ? slug : undefined} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Editing: {selectedSlug}</Typography>
              {(loading || loadingPage) && (
                <Typography variant="body2" color="text.secondary">
                  Loading page content...
                </Typography>
              )}

              <TextField
                label="Title"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Subtitle"
                value={draft.subtitle}
                onChange={(e) => setDraft((d) => ({ ...d, subtitle: e.target.value }))}
                fullWidth
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Hero Image (Recommended: 1920x600px)
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="service-page-hero-upload"
                  type="file"
                  onChange={handleHeroImageUpload}
                />
                <label htmlFor="service-page-hero-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Upload Hero Image
                  </Button>
                </label>
                <Typography variant="caption" color="text.secondary" display="block">
                  PNG/JPEG uploads are optimized and saved as WebP in the database. Leave unchanged to keep the current hero image.
                </Typography>
                {draft.heroImagePreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Box
                      component="img"
                      src={draft.heroImagePreview}
                      alt="Hero preview"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  </Box>
                )}
              </Box>

              <TextField
                label="Intro Title"
                value={draft.introTitle}
                onChange={(e) => setDraft((d) => ({ ...d, introTitle: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Intro Text"
                value={draft.introText}
                onChange={(e) => setDraft((d) => ({ ...d, introText: e.target.value }))}
                fullWidth
                multiline
                rows={3}
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Service Boxes
                </Typography>
                <ServicePageBoxesEditor
                  key={`${selectedSlug}-boxes`}
                  boxesSectionTitle={draft.boxesSectionTitle}
                  boxes={draft.contentBoxes}
                  onChange={(payload) => setDraft((d) => ({
                    ...d,
                    boxesSectionTitle: payload.boxesSectionTitle,
                    contentBoxes: payload.boxes,
                  }))}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Additional Page Sections
                </Typography>
                <ServicePageBodyEditor
                  key={`${selectedSlug}-sections`}
                  content={{
                    bodyHtml: draft.contentBodyHtml,
                    sections: draft.contentSections,
                  }}
                  onChange={(payload) => setDraft((d) => ({
                    ...d,
                    contentSections: payload.sections,
                    contentBodyHtml: payload.bodyHtml,
                  }))}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Menu Group"
                    value={draft.menuGroup}
                    onChange={(e) => setDraft((d) => ({ ...d, menuGroup: e.target.value }))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Parent Slug"
                    value={draft.parentSlug}
                    onChange={(e) => setDraft((d) => ({ ...d, parentSlug: e.target.value }))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sort Order"
                    type="number"
                    value={draft.sortOrder}
                    onChange={(e) => setDraft((d) => ({ ...d, sortOrder: e.target.value }))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={draft.isActive}
                        onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button variant="contained" onClick={handleSave} disabled={loading || loadingPage || saving}>
                  {saving ? 'Saving...' : 'Save Page'}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ManageServicePages;
